import React, { useEffect, useRef, useState } from 'react'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'

import Pagination from '../Layout/Pagination'
import { formatDate, formatDateNumber, thousandSeparator } from '../../utils/helpers'

const SalesDateListingTable = ({
  selectedAgent,
  filterData
}) => {
  const inputRef = useRef(null)
  const divRef = useRef(null)
  const [showSearchBox, setShowSearchBox] = useState(false)
  const [filteredData, setFilteredData] = useState(selectedAgent)

  const [totalCommission, setTotalCommission] = useState(null)
  const [totalPayout, setTotalPayout] = useState(null)

  const [currentPage, setCurrentPage] = useState(1);
  const userAccessType = useSelector(state => state.userAccessType)

  const handleShowSearchBox = () => {
    setShowSearchBox(!showSearchBox)
    if (setShowSearchBox) inputRef.current.focus()
  }

  const handleSearchData = (e) => {
    let searchQuery = e.target.value;

    const filteredData = selectedAgent.filter((row) => {
      return Object.values(row).some((value) => {
        if (typeof value === 'string' || typeof value === 'number') {
          if (typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())) {
            return true;
          }
          if (typeof value === 'number' && value.toString().includes(searchQuery)) {
            return true;
          }
        }
        return false;
      });
    });

    setCurrentPage(1)
    setFilteredData(filteredData);
    divRef.current.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant"
    });
  };

  const ITEMS_PER_PAGE = 10;

  const paginatedData = () => {
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    return filteredData.slice(startIdx, endIdx);
  }


  useEffect(() => {
    if (selectedAgent) {
      const totalCommission = selectedAgent.reduce((acc, item) => acc + item.CommissionAmount, 0)
      const totalPayout = selectedAgent.reduce((acc, item) => acc + item.CommissionSum, 0)

      setTotalCommission(totalCommission)
      setTotalPayout(totalPayout)
    }
  }, [selectedAgent])

  return (
    <>
      <div className='flex flex-col gap-1'>
        <div className='table-header'>
          <p className='title'>{selectedAgent[0]?.CommissionPayToUserName} - Listing Filtered By Sales Date ({formatDateNumber(filterData.StartDate)} - {formatDateNumber(filterData.EndDate)})</p>
          <div className='search-wrapper'>
            <div className='btn-search'>
              <FontAwesomeIcon
                onClick={handleShowSearchBox}
                icon={faMagnifyingGlass}
                className={'icon ' + (showSearchBox ? 'text-primary' : '')}
              />
              <input
                ref={inputRef}
                type='text'
                onInput={handleSearchData}
                onBlur={() => setShowSearchBox(false)}
                placeholder='Search for any keywords'
                className={(showSearchBox ? 'searchbox-open' : 'w-0')}
              />
            </div>
          </div>
        </div>

        <div className='border-border border rounded-[10px] shadow-md overflow-hidden flex-1 relative'>
          <div ref={divRef} className='w-full h-full overflow-scroll hide-scrollbar bg-gray-200'>
            <div className="md:w-full w-[1080px] h-full">
              <table className="w-full border-collapse bg-white">
                <thead className="border-b border-gray-200 bg-gray-200 text-gray-one sticky top-0">
                  <tr>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Sales Date
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Account
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Commission (RM)
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Name
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Branch
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Group
                    </th>
                    <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      PV Date
                    </th>
                    {(userAccessType !== "Agent" && userAccessType !== "Team Leader") && <th scope="col" className="text-[11px] font-semibold p-2 whitespace-nowrap text-center">
                      Payout Amount (RM)
                    </th>}
                  </tr>
                </thead>

                <tbody>
                  {paginatedData().map((item, idx) => (
                    <tr key={'agentsales_' + idx} className="border-gray-200 border-b text-gray-two hover:bg-gray-50">
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{formatDate(item?.CommissionSalesDate)}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{item?.CommissionAccount}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">RM {thousandSeparator(item?.CommissionAmount ?? 0)}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{item?.CommissionPayToUserName}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{item?.CommissionPayToUserBranch}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{item?.CommissionPayToUserGroupName}</td>
                      <td className="text-xs font-normal p-2 border-r whitespace-nowrap text-center">{formatDate(item?.CommissionDate)}</td>
                      {(userAccessType !== "Agent" && userAccessType !== "Team Leader") && <td className="text-xs font-normal p-2 whitespace-nowrap text-center">RM {thousandSeparator(item?.CommissionSum ?? 0)}</td>}
                    </tr>
                  ))}

                  <tr className="border-gray-200 text-gray-one bg-gray-100 sticky bottom-0">
                    <td colSpan={2} className="p-2 border-r whitespace-nowrap text-xs font-semibold text-end">Total Commission:</td>
                    <td className="text-xs font-semibold p-2 border-r whitespace-nowrap text-center">RM {thousandSeparator(totalCommission ?? 0)}</td>
                    {(userAccessType !== "Agent" && userAccessType !== "Team Leader") && <td colSpan={4} className="p-2 border-r whitespace-nowrap text-xs font-semibold text-end">Total Payout:</td>}
                    {(userAccessType !== "Agent" && userAccessType !== "Team Leader") && <td className="text-xs font-semibold p-2 whitespace-nowrap text-center">RM {thousandSeparator(totalPayout ?? 0)}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Pagination
          data={filteredData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={ITEMS_PER_PAGE}
        />
      </div>
    </>
  )
}

export default SalesDateListingTable